/**
 * Valintakomponenttien (select, radio-group) vaihtoehtolistan jäsennys
 * (WP-ABP1). Puhdas funktio ilman DOMia — DOM-puoli on runtime.ts:n
 * `applySlotToDom`in `options`-haarassa.
 *
 * <p>Sidottu `options` on se palikka jolla pudotusvalikon saa täytettyä
 * datalähteestä: `${customers.data.map(r => ({value: r.id, label: r.name}))}`.
 * Lausekekieli osaa objektiliteraalit ja `map`in jo ennestään
 * (`expression.ts`), joten tässä tarvitaan vain vastaanottopää.
 */

/** Yksi normalisoitu vaihtoehto. Molemmat kentät ovat aina merkkijonoja. */
export interface OptionEntry {
  value: string;
  label: string;
}

/**
 * Jäsennä vaihtoehtolista: taulukko sellaisenaan (sidottu lauseke) tai
 * JSON-taulukko merkkijonona (katalogin textarea-literaali).
 *
 * <p>Paluuarvo `null` tarkoittaa **"lukukelvoton — säilytä edelliset
 * vaihtoehdot"**, ja se on tietoinen ero {@link coerceRows}-konventioon
 * (joka pudottaa tyhjään taulukkoon). Syy: valikko jonka sisältö katoaa
 * hetkellisen parse-virheen takia menettää samalla käyttäjän valinnan.
 * Tyhjä taulukko `[]` sen sijaan on **aito tyhjä tulos** (hakuehto ei
 * täsmännyt mihinkään) ja tyhjentää valikon.
 */
export function parseOptionList(raw: unknown): OptionEntry[] | null {
  if (Array.isArray(raw)) {
    return normalize(raw);
  }
  if (typeof raw === 'string') {
    const text = raw.trim();
    if (!text) return null;
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) return normalize(parsed);
    } catch {
      // Rikkinäinen JSON (esim. keskeneräinen textarea) → säilytä edelliset.
    }
  }
  return null;
}

/**
 * Alkiot muotoon {value, label}. Ei-objektialkiot ohitetaan; `label`
 * oletusarvo on `value`, jolloin `[{"value":"open"}]` renderöityy järkevästi
 * ilman erillistä labelia.
 */
function normalize(list: unknown[]): OptionEntry[] {
  const out: OptionEntry[] = [];
  for (const entry of list) {
    if (!entry || typeof entry !== 'object') continue;
    const o = entry as Record<string, unknown>;
    if (o.value === undefined || o.value === null) continue;
    const value = String(o.value);
    const label = o.label === undefined || o.label === null ? value : String(o.label);
    out.push({ value, label });
  }
  return out;
}

/**
 * Säilyykö nykyinen valinta kun vaihtoehdot vaihtuvat? Valinta säilyy vain
 * jos sama arvo on yhä listalla — muuten se tyhjenee, ja kutsujan on
 * julkaistava tyhjä `value`-slot. Ilman tyhjennystä lomake lähettäisi arvon
 * jota käyttäjä ei enää näe valikossa.
 */
export function keepSelection(options: OptionEntry[], current: string): string {
  return options.some((o) => o.value === current) ? current : '';
}
